import { useState, useCallback, useMemo } from 'react';
import { event as gaEvent } from '../utils/analytics';
import { useWeightUnit } from './useWeightUnit';
import { formatMassDisplayText } from '../utils/weightUnits';

/**
 * Meal types for organizing the daily meal plan
 */
export const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snacks'];

/**
 * Number of days in a week plan
 */
export const WEEK_DAYS = 7;

/**
 * FreshFitLab source identifier (menus without affiliateId)
 */
export const FRESHMENULAB_SOURCE = 'freshmenulab';

/**
 * Day names for display
 */
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Create empty day structure
 */
const createEmptyDay = () => ({
  breakfast: { foods: [], menuItems: [] },
  lunch: { foods: [], menuItems: [] },
  dinner: { foods: [], menuItems: [] },
  snacks: { foods: [], menuItems: [] }
});

/**
 * Create initial week plan starting from a given date
 */
const createWeekPlan = (startDate = new Date()) => {
  const days = [];
  for (let i = 0; i < WEEK_DAYS; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    days.push({
      dayNumber: i + 1,
      date: date.toISOString().split('T')[0], // YYYY-MM-DD format
      dayName: DAY_NAMES[date.getDay()],
      meals: createEmptyDay()
    });
  }
  return days;
};

/**
 * Custom hook for managing nutrition planner meal plan
 * 
 * Supports:
 * - 7-day week planning
 * - Individual foods from USDA database (per 100g)
 * - Menu items from FreshFitLab (per serving)
 * - Organization by meal type (breakfast, lunch, dinner, snacks)
 * - Daily and weekly nutrition totals
 * - Single-source menu restriction for proper attribution
 */
export const useNutritionCart = () => {
  const weightUnit = useWeightUnit();
  // Week plan with 7 days
  const [weekPlan, setWeekPlan] = useState(() => createWeekPlan());
  const [currentDay, setCurrentDayState] = useState(1);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Track menu source for attribution (null = no menus yet, 'freshmenulab' or affiliateId)
  const [menuSource, setMenuSource] = useState(null);

  /**
   * Get the current day's meal plan
   */
  const getCurrentDayPlan = useCallback(() => {
    return weekPlan.find(d => d.dayNumber === currentDay) || weekPlan[0];
  }, [weekPlan, currentDay]);

  /**
   * Get meal plan for current day (backward compat with mealPlan)
   */
  const mealPlan = useMemo(() => {
    return getCurrentDayPlan()?.meals || createEmptyDay();
  }, [getCurrentDayPlan]);

  /**
   * Parse nutrition value from menu item
   */
  const parseNutrition = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const num = parseFloat(value.replace(/[^0-9.]/g, ''));
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

  /**
   * Add a food item to a meal on the current day
   */
  const addFood = useCallback((food, mealType = 'snacks', quantity = 1, servingSize = 100) => {
    setWeekPlan(prev => prev.map(day => {
      if (day.dayNumber !== currentDay) return day;
      
      const meal = day.meals[mealType] || { foods: [], menuItems: [] };
      const existingIndex = meal.foods.findIndex(item => item.food.fdcId === food.fdcId);
      
      let updatedFoods;
      if (existingIndex >= 0) {
        updatedFoods = [...meal.foods];
        updatedFoods[existingIndex] = {
          ...updatedFoods[existingIndex],
          quantity: updatedFoods[existingIndex].quantity + quantity
        };
      } else {
        updatedFoods = [...meal.foods, { food, quantity, servingSize }];
      }
      
      return {
        ...day,
        meals: {
          ...day.meals,
          [mealType]: { ...meal, foods: updatedFoods }
        }
      };
    }));
    gaEvent({ action: 'add_food_to_plan', category: 'NutritionPlanner', label: food.description || food.fdcId });
  }, [currentDay]);

  /**
   * Get the source identifier for a menu item
   * Returns 'freshmenulab' for admin menus, or the affiliateId for affiliate menus
   */
  const getMenuItemSource = useCallback((menuItem) => {
    // If menu has an affiliateId, it belongs to that affiliate
    // Otherwise, it's a FreshFitLab/admin menu
    return menuItem.affiliateId || FRESHMENULAB_SOURCE;
  }, []);

  /**
   * Check if a menu item can be added to the plan.
   * Always allows adding -- affiliate restriction is enforced at checkout/Instacart only.
   * Admin/FreshFitLab recipes are always compatible with any single affiliate's menus.
   */
  const canAddMenuItem = useCallback((menuItem) => {
    const itemSource = getMenuItemSource(menuItem);
    const itemIsAdmin = !menuItem.affiliateId;
    const currentIsAdmin = menuSource === FRESHMENULAB_SOURCE;
    // Only warn when mixing two different affiliates (not admin + affiliate)
    const isMixed = menuSource && itemSource !== menuSource && !itemIsAdmin && !currentIsAdmin;
    return {
      canAdd: true,
      isMixed,
      reason: isMixed
        ? 'Note: this recipe is from a different affiliate than others in your plan. Mixed-affiliate plans cannot be sent to Instacart as a single order.'
        : null,
      currentSource: menuSource
    };
  }, [menuSource, getMenuItemSource]);

  /**
   * Check if the current plan has menus from multiple *different* affiliates.
   * Admin/FreshFitLab recipes (no affiliateId) are always compatible with any
   * single affiliate's recipes, so they are excluded from this check.
   * Only returns true when 2+ distinct affiliate IDs are present.
   */
  const hasMultipleSources = useMemo(() => {
    const affiliateIds = new Set();
    for (const day of weekPlan) {
      for (const mealType of MEAL_TYPES) {
        const meal = day.meals[mealType];
        if (!meal?.menuItems) continue;
        for (const item of meal.menuItems) {
          const menuData = item.menuItem || item;
          if (menuData.affiliateId) {
            affiliateIds.add(menuData.affiliateId.toString());
          }
        }
      }
    }
    return affiliateIds.size > 1;
  }, [weekPlan]);

  /**
   * Add a menu item to a meal on the current day
   */
  const addMenuItem = useCallback((menuItem, mealType = 'lunch', servings = 1) => {
    const itemSource = getMenuItemSource(menuItem);
    
    if (!menuSource) {
      setMenuSource(itemSource);
    }
    
    setWeekPlan(prev => prev.map(day => {
      if (day.dayNumber !== currentDay) return day;
      
      const meal = day.meals[mealType] || { foods: [], menuItems: [] };
      const existingIndex = meal.menuItems.findIndex(item => item.menuItem._id === menuItem._id);
      
      let updatedMenuItems;
      if (existingIndex >= 0) {
        updatedMenuItems = [...meal.menuItems];
        updatedMenuItems[existingIndex] = {
          ...updatedMenuItems[existingIndex],
          servings: updatedMenuItems[existingIndex].servings + servings
        };
      } else {
        // Store the affiliateId with the menu item for future reference
        const menuItemWithSource = {
          ...menuItem,
          affiliateId: menuItem.affiliateId || null
        };
        updatedMenuItems = [...meal.menuItems, { menuItem: menuItemWithSource, servings }];
      }
      
      return {
        ...day,
        meals: {
          ...day.meals,
          [mealType]: { ...meal, menuItems: updatedMenuItems }
        }
      };
    }));
    gaEvent({ action: 'add_recipe_to_plan', category: 'NutritionPlanner', label: menuItem.title || menuItem._id });
  }, [currentDay, menuSource, getMenuItemSource]);

  /**
   * Remove a food item from a meal on the current day
   */
  const removeFood = useCallback((fdcId, mealType) => {
    setWeekPlan(prev => prev.map(day => {
      if (day.dayNumber !== currentDay) return day;
      
      return {
        ...day,
        meals: {
          ...day.meals,
          [mealType]: {
            ...day.meals[mealType],
            foods: day.meals[mealType].foods.filter(item => item.food.fdcId !== fdcId)
          }
        }
      };
    }));
  }, [currentDay]);

  /**
   * Remove a menu item from a meal on the current day
   */
  const removeMenuItem = useCallback((menuItemId, mealType) => {
    setWeekPlan(prev => prev.map(day => {
      if (day.dayNumber !== currentDay) return day;
      
      return {
        ...day,
        meals: {
          ...day.meals,
          [mealType]: {
            ...day.meals[mealType],
            menuItems: day.meals[mealType].menuItems.filter(item => item.menuItem._id !== menuItemId)
          }
        }
      };
    }));
  }, [currentDay]);

  /**
   * Update food quantity on the current day
   */
  const updateFoodQuantity = useCallback((fdcId, mealType, quantity) => {
    if (quantity <= 0) {
      removeFood(fdcId, mealType);
      return;
    }
    
    setWeekPlan(prev => prev.map(day => {
      if (day.dayNumber !== currentDay) return day;
      
      return {
        ...day,
        meals: {
          ...day.meals,
          [mealType]: {
            ...day.meals[mealType],
            foods: day.meals[mealType].foods.map(item =>
              item.food.fdcId === fdcId ? { ...item, quantity } : item
            )
          }
        }
      };
    }));
  }, [currentDay, removeFood]);

  /**
   * Update menu item servings on the current day
   */
  const updateMenuItemServings = useCallback((menuItemId, mealType, servings) => {
    if (servings <= 0) {
      removeMenuItem(menuItemId, mealType);
      return;
    }
    
    setWeekPlan(prev => prev.map(day => {
      if (day.dayNumber !== currentDay) return day;
      
      return {
        ...day,
        meals: {
          ...day.meals,
          [mealType]: {
            ...day.meals[mealType],
            menuItems: day.meals[mealType].menuItems.map(item =>
              item.menuItem._id === menuItemId ? { ...item, servings } : item
            )
          }
        }
      };
    }));
  }, [currentDay, removeMenuItem]);

  /**
   * Navigate to next day
   */
  const nextDay = useCallback(() => {
    setCurrentDayState(prev => Math.min(prev + 1, WEEK_DAYS));
  }, []);

  /**
   * Navigate to previous day
   */
  const previousDay = useCallback(() => {
    setCurrentDayState(prev => Math.max(prev - 1, 1));
  }, []);

  /**
   * Set current day directly
   */
  const setCurrentDay = useCallback((dayNumber) => {
    if (dayNumber >= 1 && dayNumber <= WEEK_DAYS) {
      setCurrentDayState(dayNumber);
    }
  }, []);

  /**
   * Check if food is in current day's plan
   */
  const isFoodInPlan = useCallback((fdcId) => {
    const dayPlan = getCurrentDayPlan();
    return MEAL_TYPES.some(mealType => 
      dayPlan.meals[mealType].foods.some(item => item.food.fdcId === fdcId)
    );
  }, [getCurrentDayPlan]);

  /**
   * Check if menu item is in current day's plan
   */
  const isMenuItemInPlan = useCallback((menuItemId) => {
    const dayPlan = getCurrentDayPlan();
    return MEAL_TYPES.some(mealType => 
      dayPlan.meals[mealType].menuItems.some(item => item.menuItem._id === menuItemId)
    );
  }, [getCurrentDayPlan]);

  /**
   * Clear all items from the week plan
   */
  const clearPlan = useCallback(() => {
    setWeekPlan(createWeekPlan(new Date(startDate)));
    setCurrentDayState(1);
    setMenuSource(null); // Reset menu source for new attribution
  }, [startDate]);

  /**
   * Set the start date for the week plan
   * This will recreate the week plan with new dates
   */
  const setWeekStartDate = useCallback((newStartDate) => {
    const dateObj = new Date(newStartDate);
    setStartDate(dateObj.toISOString().split('T')[0]);
    setWeekPlan(createWeekPlan(dateObj));
    setCurrentDayState(1);
  }, []);

  /**
   * Clear a specific meal on the current day
   */
  const clearMeal = useCallback((mealType) => {
    setWeekPlan(prev => prev.map(day => {
      if (day.dayNumber !== currentDay) return day;
      return {
        ...day,
        meals: {
          ...day.meals,
          [mealType]: { foods: [], menuItems: [] }
        }
      };
    }));
  }, [currentDay]);

  /**
   * Clear a specific day
   */
  const clearDay = useCallback((dayNumber) => {
    setWeekPlan(prev => prev.map(day => {
      if (day.dayNumber !== dayNumber) return day;
      return {
        ...day,
        meals: createEmptyDay()
      };
    }));
  }, []);

  /**
   * Calculate nutrition totals for a specific meal on a specific day
   */
  const getMealTotalsForDay = useCallback((dayNumber, mealType) => {
    const day = weekPlan.find(d => d.dayNumber === dayNumber);
    if (!day) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    
    const meal = day.meals[mealType];
    const totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    
    meal.foods.forEach(item => {
      const scaleFactor = (item.servingSize / 100) * item.quantity;
      const nutrients = item.food.nutrients || {};
      totals.calories += (nutrients.calories || 0) * scaleFactor;
      totals.protein += (nutrients.protein || 0) * scaleFactor;
      totals.carbs += (nutrients.carbs || 0) * scaleFactor;
      totals.fat += (nutrients.fat || 0) * scaleFactor;
    });
    
    meal.menuItems.forEach(item => {
      const nutrition = item.menuItem.nutrition || {};
      totals.calories += parseNutrition(nutrition.Calories) * item.servings;
      totals.protein += parseNutrition(nutrition.Protein) * item.servings;
      totals.carbs += parseNutrition(nutrition.Carbohydrates) * item.servings;
      totals.fat += parseNutrition(nutrition.Fat) * item.servings;
    });
    
    return {
      calories: Math.round(totals.calories),
      protein: Math.round(totals.protein * 10) / 10,
      carbs: Math.round(totals.carbs * 10) / 10,
      fat: Math.round(totals.fat * 10) / 10
    };
  }, [weekPlan]);

  /**
   * Calculate nutrition totals for a meal on current day (backward compat)
   */
  const getMealTotals = useCallback((mealType) => {
    return getMealTotalsForDay(currentDay, mealType);
  }, [currentDay, getMealTotalsForDay]);

  /**
   * Round to 1 decimal place for clean display
   */
  const round1 = (num) => Math.round(num * 10) / 10;

  /**
   * Calculate daily totals for a specific day
   */
  const getDayTotals = useCallback((dayNumber) => {
    const totals = MEAL_TYPES.reduce((acc, mealType) => {
      const mealTotals = getMealTotalsForDay(dayNumber, mealType);
      return {
        calories: acc.calories + mealTotals.calories,
        protein: acc.protein + mealTotals.protein,
        carbs: acc.carbs + mealTotals.carbs,
        fat: acc.fat + mealTotals.fat
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
    
    // Round all values to 1 decimal place
    return {
      calories: round1(totals.calories),
      protein: round1(totals.protein),
      carbs: round1(totals.carbs),
      fat: round1(totals.fat)
    };
  }, [getMealTotalsForDay]);

  /**
   * Daily totals for current day (backward compat)
   */
  const dailyTotals = useMemo(() => {
    return getDayTotals(currentDay);
  }, [currentDay, getDayTotals]);

  /**
   * Calculate week totals (all 7 days combined)
   */
  const getWeekTotals = useMemo(() => {
    const totals = weekPlan.reduce((acc, day) => {
      const dayTotals = getDayTotals(day.dayNumber);
      return {
        calories: acc.calories + dayTotals.calories,
        protein: acc.protein + dayTotals.protein,
        carbs: acc.carbs + dayTotals.carbs,
        fat: acc.fat + dayTotals.fat
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
    
    // Round all values to 1 decimal place
    return {
      calories: round1(totals.calories),
      protein: round1(totals.protein),
      carbs: round1(totals.carbs),
      fat: round1(totals.fat)
    };
  }, [weekPlan, getDayTotals]);

  /**
   * Calculate weekly progress against targets
   */
  const calculateWeekProgress = useCallback((dailyTargets) => {
    if (!dailyTargets) return null;
    
    const weeklyTargets = {
      calories: dailyTargets.dailyCalories * WEEK_DAYS,
      protein: dailyTargets.dailyProtein * WEEK_DAYS,
      carbs: dailyTargets.dailyCarbs * WEEK_DAYS,
      fat: dailyTargets.dailyFat * WEEK_DAYS
    };
    
    return {
      calories: weeklyTargets.calories > 0 
        ? Math.round((getWeekTotals.calories / weeklyTargets.calories) * 100) 
        : 0,
      protein: weeklyTargets.protein > 0 
        ? Math.round((getWeekTotals.protein / weeklyTargets.protein) * 100) 
        : 0,
      carbs: weeklyTargets.carbs > 0 
        ? Math.round((getWeekTotals.carbs / weeklyTargets.carbs) * 100) 
        : 0,
      fat: weeklyTargets.fat > 0 
        ? Math.round((getWeekTotals.fat / weeklyTargets.fat) * 100) 
        : 0
    };
  }, [getWeekTotals]);

  /**
   * Calculate daily progress for current day
   */
  const calculateDailyProgress = useCallback((dailyTargets) => {
    if (!dailyTargets) return null;
    
    return {
      calories: dailyTargets.dailyCalories > 0 
        ? Math.round((dailyTotals.calories / dailyTargets.dailyCalories) * 100) 
        : 0,
      protein: dailyTargets.dailyProtein > 0 
        ? Math.round((dailyTotals.protein / dailyTargets.dailyProtein) * 100) 
        : 0,
      carbs: dailyTargets.dailyCarbs > 0 
        ? Math.round((dailyTotals.carbs / dailyTargets.dailyCarbs) * 100) 
        : 0,
      fat: dailyTargets.dailyFat > 0 
        ? Math.round((dailyTotals.fat / dailyTargets.dailyFat) * 100) 
        : 0
    };
  }, [dailyTotals]);

  /**
   * Get item count for a specific day
   */
  const getDayItemCount = useCallback((dayNumber) => {
    const day = weekPlan.find(d => d.dayNumber === dayNumber);
    if (!day) return 0;
    
    return MEAL_TYPES.reduce((count, mealType) => {
      return count + day.meals[mealType].foods.length + day.meals[mealType].menuItems.length;
    }, 0);
  }, [weekPlan]);

  /**
   * Total item count for current day
   */
  const totalItemCount = useMemo(() => {
    return getDayItemCount(currentDay);
  }, [currentDay, getDayItemCount]);

  /**
   * Total item count across all days
   */
  const weekItemCount = useMemo(() => {
    return weekPlan.reduce((count, day) => count + getDayItemCount(day.dayNumber), 0);
  }, [weekPlan, getDayItemCount]);

  /**
   * Get meal item count for current day
   */
  const getMealItemCount = useCallback((mealType) => {
    const meal = mealPlan[mealType];
    return meal.foods.length + meal.menuItems.length;
  }, [mealPlan]);

  /**
   * Get days with items (for progress indicator)
   */
  const getDaysWithItems = useMemo(() => {
    return weekPlan.filter(day => getDayItemCount(day.dayNumber) > 0).length;
  }, [weekPlan, getDayItemCount]);

  /**
   * Get week summary for display
   */
  const getWeekSummary = useMemo(() => {
    return weekPlan.map(day => ({
      dayNumber: day.dayNumber,
      date: day.date,
      dayName: day.dayName,
      itemCount: getDayItemCount(day.dayNumber),
      totals: getDayTotals(day.dayNumber),
      hasItems: getDayItemCount(day.dayNumber) > 0
    }));
  }, [weekPlan, getDayItemCount, getDayTotals]);

  /**
   * Convert entire week plan to Instacart line items
   */
  const toInstacartItems = useCallback(() => {
    const items = [];
    
    weekPlan.forEach(day => {
      MEAL_TYPES.forEach(mealType => {
        const meal = day.meals[mealType];
        
        meal.foods.forEach(item => {
          const totalGrams = item.servingSize * item.quantity;
          let displayQuantity, displayUnit;
          
          if (totalGrams >= 1000) {
            displayQuantity = Math.round(totalGrams / 1000 * 10) / 10;
            displayUnit = 'kg';
          } else if (totalGrams >= 100) {
            displayQuantity = Math.round(totalGrams / 100);
            displayUnit = '100g';
          } else {
            displayQuantity = totalGrams;
            displayUnit = 'g';
          }
          
          items.push({
            name: item.food.description,
            quantity: displayQuantity,
            unit: displayUnit,
            display_text: formatMassDisplayText(totalGrams, 'g', item.food.description, weightUnit),
            source: 'food',
            dayNumber: day.dayNumber
          });
        });
        
        meal.menuItems.forEach(item => {
          const ingredients = item.menuItem.ingredients || [];
          ingredients.forEach(ing => {
            const scaledQty = (ing.quantity || 1) * item.servings;
            items.push({
              name: ing.name,
              quantity: scaledQty,
              unit: ing.unit || 'each',
              display_text: formatMassDisplayText(scaledQty, ing.unit || 'each', ing.name, weightUnit),
              source: 'menuItem',
              menuItemTitle: item.menuItem.title,
              dayNumber: day.dayNumber
            });
          });
        });
      });
    });
    
    return items;
  }, [weekPlan, weightUnit]);

  // Backward compatibility - flat items list for current day
  const items = useMemo(() => {
    const allItems = [];
    MEAL_TYPES.forEach(mealType => {
      mealPlan[mealType].foods.forEach(item => {
        allItems.push({ ...item, mealType, type: 'food' });
      });
      mealPlan[mealType].menuItems.forEach(item => {
        allItems.push({ ...item, mealType, type: 'menuItem' });
      });
    });
    return allItems;
  }, [mealPlan]);

  // Backward compatibility
  const addItem = useCallback((food, quantity = 1, servingSize = 100) => {
    addFood(food, 'snacks', quantity, servingSize);
  }, [addFood]);

  const removeItem = useCallback((fdcId) => {
    MEAL_TYPES.forEach(mealType => {
      removeFood(fdcId, mealType);
    });
  }, [removeFood]);

  const updateQuantity = useCallback((fdcId, quantity) => {
    MEAL_TYPES.forEach(mealType => {
      if (mealPlan[mealType].foods.some(item => item.food.fdcId === fdcId)) {
        updateFoodQuantity(fdcId, mealType, quantity);
      }
    });
  }, [mealPlan, updateFoodQuantity]);

  const isInCart = isFoodInPlan;
  const clearCart = clearPlan;
  const itemCount = totalItemCount;
  const totals = dailyTotals;

  const calculateProgress = useCallback((targets) => {
    if (!targets) return null;
    return {
      calories: targets.perMealCalories > 0 
        ? Math.round((dailyTotals.calories / targets.perMealCalories) * 100) 
        : 0,
      protein: targets.perMealProtein > 0 
        ? Math.round((dailyTotals.protein / targets.perMealProtein) * 100) 
        : 0,
      carbs: targets.perMealCarbs > 0 
        ? Math.round((dailyTotals.carbs / targets.perMealCarbs) * 100) 
        : 0,
      fat: targets.perMealFat > 0 
        ? Math.round((dailyTotals.fat / targets.perMealFat) * 100) 
        : 0
    };
  }, [dailyTotals]);

  /**
   * Transform a meal from database format to cart format
   * Handles both new flat format and old nested format for backwards compatibility
   */
  const transformMealFromDb = (meal) => {
    if (!meal) return { foods: [], menuItems: [] };
    
    return {
      foods: (meal.foods || []).map(item => {
        // Handle both flat format (from DB) and nested format (if already in cart format)
        const foodData = item.food || item;
        return {
          food: {
            fdcId: foodData.fdcId,
            description: foodData.description,
            category: foodData.category,
            brandOwner: foodData.brandOwner,
            nutrients: foodData.nutrients || {}
          },
          quantity: item.quantity || 1,
          servingSize: item.servingSize || 100
        };
      }),
      menuItems: (meal.menuItems || []).map(item => {
        // Handle both flat format (from DB) and nested format (if already in cart format)
        const menuData = item.menuItem || item;
        return {
          menuItem: {
            _id: menuData.menuItemId || menuData._id,
            title: menuData.title,
            image: menuData.image,
            nutrition: menuData.nutrition || {}
          },
          servings: item.servings || 1
        };
      })
    };
  };

  /**
   * Load a week plan from saved data
   * @param {Array} savedDays - Array of day objects from saved plan
   */
  const loadWeekPlan = useCallback((savedDays) => {
    if (!savedDays || savedDays.length === 0) return;
    
    console.log('Loading saved days:', savedDays);
    
    // Detect menu source from saved data
    let detectedSource = null;
    savedDays.forEach(day => {
      if (!day.meals) return;
      MEAL_TYPES.forEach(mealType => {
        const meal = day.meals[mealType];
        if (!meal?.menuItems) return;
        meal.menuItems.forEach(item => {
          const menuData = item.menuItem || item;
          const itemAffiliateId = menuData.affiliateId;
          const itemSource = itemAffiliateId || FRESHMENULAB_SOURCE;
          
          if (!detectedSource) {
            detectedSource = itemSource;
          }
          // Note: If sources are mixed (shouldn't happen with new code), 
          // we use the first detected source
        });
      });
    });
    
    if (detectedSource) {
      setMenuSource(detectedSource);
    }
    
    setWeekPlan(prev => {
      // Create a map for quick lookup
      const savedDaysMap = new Map(savedDays.map(d => [d.dayNumber, d]));
      
      return prev.map(day => {
        const savedDay = savedDaysMap.get(day.dayNumber);
        if (!savedDay || !savedDay.meals) return day;
        
        // Transform saved meals from database format to cart format
        return {
          ...day,
          meals: {
            breakfast: transformMealFromDb(savedDay.meals.breakfast),
            lunch: transformMealFromDb(savedDay.meals.lunch),
            dinner: transformMealFromDb(savedDay.meals.dinner),
            snacks: transformMealFromDb(savedDay.meals.snacks)
          }
        };
      });
    });
    
    setCurrentDayState(1);
  }, []);

  return {
    // Week planning API
    weekPlan,
    currentDay,
    startDate,
    setCurrentDay,
    setWeekStartDate,
    nextDay,
    previousDay,
    getDayTotals,
    getDayItemCount,
    getWeekTotals,
    getWeekSummary,
    calculateWeekProgress,
    weekItemCount,
    getDaysWithItems,
    clearDay,
    loadWeekPlan,
    WEEK_DAYS,
    
    // Menu source tracking for attribution
    menuSource,
    getMenuItemSource,
    canAddMenuItem,
    hasMultipleSources,
    FRESHMENULAB_SOURCE,
    
    // Current day API (backward compat with mealPlan)
    mealPlan,
    MEAL_TYPES,
    addFood,
    addMenuItem,
    removeFood,
    removeMenuItem,
    updateFoodQuantity,
    updateMenuItemServings,
    isFoodInPlan,
    isMenuItemInPlan,
    clearPlan,
    clearMeal,
    getMealTotals,
    dailyTotals,
    calculateDailyProgress,
    totalItemCount,
    getMealItemCount,
    
    // Backward compatible API
    items,
    addItem,
    removeItem,
    updateQuantity,
    isInCart,
    clearCart,
    totals,
    calculateProgress,
    toInstacartItems,
    itemCount
  };
};

export default useNutritionCart;
